/**
 * ``SnapshotLayer`` — non-visual React component that wires a
 * MapLibre GeoJSON source + WebGL circle layer to a snapshot
 * response, then patches the source in place as SSE ``status``
 * deltas arrive.
 *
 * Stream A PR 2 / commits M2 + M3. M2 introduced the snapshot
 * attach/detach lifecycle; M3 layers SSE delta-merge on top via a
 * second effect that runs on each incoming ``status`` message and
 * calls :func:`maplibregl.GeoJSONSource#setData` against the
 * already-attached source. ``event``-kind SSE messages are routed
 * past this component — they are not Producers and don't belong
 * in the snapshot source.
 *
 * # Why a separate component
 *
 * The :class:`Map` component owns the container ref + the
 * :func:`useMap` lifecycle. Source + layer registration is a
 * concern that needs to fire **after** the map is loaded
 * (MapLibre rejects ``addSource`` before ``load``). Splitting the
 * layer into its own component lets the Map gate the mount on the
 * ``onMapReady`` callback; the layer then mounts knowing the map
 * instance is live, registers its source / layer, and tears down
 * on unmount.
 *
 * # Why a separate impl file
 *
 * ESLint's ``react-refresh/only-export-components`` rule warns
 * when a ``.tsx`` module exports non-component values alongside
 * components. The helper functions + constants live in
 * :file:`snapshot-layer-impl.ts` so Fast Refresh keeps working
 * cleanly on this component during dev.
 *
 * # SSE delta-merge model (REQ-UI-006)
 *
 * The pure :func:`mergeStatusIntoFeatureCollection` in
 * :file:`sse-merge.ts` does the work; this component just calls
 * it from an effect and writes the result via ``setData``. Per
 * REQ-UI-006 the budget is < 100 ms per event; the merge is O(N)
 * where N is the snapshot's Producer count (~150 at MVP), so the
 * pure merge completes in well under 1 ms; the dominant cost is
 * MapLibre's WebGL paint, also well under the budget.
 *
 * Cross-references:
 *   - REQ-UI-002 (WebGL marker rendering).
 *   - REQ-UI-003 / REQ-UI-004 (snapshot orchestration + 500 ms render).
 *   - REQ-UI-006 (live update marker patch ≤ 100 ms).
 *   - REQ-UI-026 (the data driving the layer comes from a
 *     TanStack Query result).
 *   - ADR-016 §1 + §3 + §4.
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md
 *     §"Map markers and glyphs".
 *   - docs/engineering/detail/storyboards/flows/A-sse-recovery/README.md
 *     §"Frame 4 — Reconnection succeeds; snapshot refresh".
 */
import { useEffect, useRef } from "react";
import type { GeoJSONSource, Map as MapLibreMap } from "maplibre-gl";
import {
  SNAPSHOT_SOURCE_ID,
  attachSnapshotLayer,
  detachSnapshotLayer,
  reapplySnapshotPlatformStyle,
  setSelectedPlatformHighlight,
  snapshotToFeatureCollection,
} from "@/map/snapshot-layer-impl";
import { useMapSelectedIdForKind } from "@/selection/selection-store";
import { isMapUsable } from "@/map/map-ready";
import { stableOverrideKey, type PlatformColorConfig } from "@/map/marker-styling";
import type { SnapshotResponse } from "@/map/snapshot-types";
import { useSseMessages, type SseSubscribe } from "@/map/sse-subscription";
import {
  mergeStatusIntoFeatureCollection,
  narrowToMarkerCollection,
  type MarkerFeatureCollection,
} from "@/map/sse-merge";

/**
 * Props for :func:`SnapshotLayer`.
 */
export interface SnapshotLayerProps {
  /** Live MapLibre map instance. Caller (the Map component) only
   *  mounts :func:`SnapshotLayer` after the map's ``load`` event
   *  has fired — see ``Map.tsx``. */
  map: MapLibreMap;
  /** Latest snapshot response, or ``null`` while loading. Null
   *  renders nothing (the layer is not attached). When the
   *  snapshot transitions from null → response, the layer attaches;
   *  on subsequent updates, the layer's source data is replaced
   *  via ``setData`` (M2 cycle: cleanup detaches the layer; the
   *  next render re-attaches — the fast-path bypassing the
   *  React-component lifecycle is reserved for M3's SSE deltas). */
  snapshot: SnapshotResponse | null;
  /**
   * The Map's ``connection.subscribe``, or ``null`` before a connection
   * exists. Each arriving ``status`` message is merged into the live source
   * via :func:`maplibregl.GeoJSONSource#setData`; other kinds are routed
   * elsewhere and ignored here.
   *
   * A subscription rather than a latest-message value (issue #739): markers
   * merge **per Producer**, so a flush carrying frames for several Producers
   * used to move only the last one's marker and leave the rest sitting at
   * stale positions until their next frame.
   */
  subscribe?: SseSubscribe | null;
  /**
   * The active Profile's platform marker colour config (``map.platform``
   * ``color_mode`` + ``single_color``; ADR-033 §9.2). Threaded into the
   * marker paint (REQ-UI-086): ``by_status`` (default) keeps the status
   * palette; ``single`` paints every marker the Profile colour. A
   * config change re-applies the paint in place (no teardown). Omit for
   * the legacy ``by_status`` behaviour.
   */
  platformColor?: PlatformColorConfig;
  /**
   * The active Profile's ``map.platform.glyph`` marker shape (GLYPHS batch;
   * REQ-UI-086; the closed :data:`map/glyphs.GLYPH_IDS` vocabulary, default
   * ``triangle``). Sets the SDF the individual markers paint; an unknown id
   * falls back to ``circle``. A shape change re-applies the glyph's
   * ``icon-image`` in place (no teardown). Cluster bubbles are unaffected.
   */
  platformGlyph?: string;
}

/**
 * Non-visual React component that owns the snapshot layer's
 * MapLibre lifecycle + applies SSE deltas in place.
 *
 * Renders no DOM (the layer is WebGL-only inside MapLibre's
 * canvas). The component exists for the standard React
 * mount/unmount semantics — when the parent unmounts (the Map
 * leaves the viewport), the layer cleans up.
 *
 * Cross-references:
 *   - REQ-UI-002 (WebGL).
 *   - REQ-UI-006 (SSE delta-merge).
 *   - REQ-UI-026 (server state via TanStack Query).
 *   - ADR-016 §4.
 */
export function SnapshotLayer(props: SnapshotLayerProps): null {
  const { map, snapshot, subscribe = null, platformColor, platformGlyph } = props;
  // The snapshot ring tracks the MAP selection slot (REQ-UI-051 + the
  // 2026-06-09 two-slot redesign): a plain Platforms-list left-click sets
  // ``inspected`` only and must NOT move the ring; a map marker click sets
  // both slots. See :file:`@/selection/selection-store` §"The two slots".
  const selectedPlatformId = useMapSelectedIdForKind("platform");

  // Depend on the config's primitive fields (not the object identity, which can
  // be a fresh literal each render) so the data-update effect re-runs — and the
  // attach re-applies the marker paint — exactly when the Profile colour
  // mode/colour actually changes (REQ-UI-086).
  const platformColorMode = platformColor?.mode;
  const platformSingleColor = platformColor?.singleColor;
  // The per-status override map (``map.platform.status_colors``) is an object;
  // serialize it to a stable primitive so it can be an effect dep (the repaint
  // must fire when a status override changes; inc-items 4/5, REQ-UI-086). Keys
  // are sorted so an unordered map never spuriously re-paints.
  const platformStatusColors = platformColor?.statusColors;
  const platformStatusColorsKey = stableOverrideKey(platformStatusColors);

  // Track the source's current ``FeatureCollection`` in a ref so
  // the SSE-delta effect can merge against the latest data without
  // re-reading from MapLibre. MapLibre's :class:`GeoJSONSource`
  // doesn't expose its data; mirroring it here lets the merge stay
  // referentially-correct.
  const featureCollectionRef = useRef<MarkerFeatureCollection | null>(null);

  // Source lifecycle effect — bound to ``map`` only. The cleanup
  // detaches the source + layer ONCE on unmount (or if ``map``
  // itself changes, which is rare — only on a full Map remount).
  //
  // Decoupled from snapshot transitions per the 2026-05-27
  // rendering-cascade fix: previously the cleanup ran on every
  // snapshot change (deps were ``[map, snapshot]``), tearing down
  // the source and recreating it on every pan/zoom re-fetch. That
  // pattern caused (a) visible flicker as the source flashed
  // between renders, and (b) — more subtly — a window where
  // MapLibre's clustering source was in flux during a zoom
  // transition, which at zoom > :const:`CLUSTER_MAX_ZOOM` (= 6)
  // left individual markers unrendered. With the source held
  // stable, snapshot updates flow through the data-update effect
  // below via :meth:`GeoJSONSource.setData`.
  useEffect(() => {
    return () => {
      detachSnapshotLayer(map);
      featureCollectionRef.current = null;
    };
  }, [map]);

  // Data-update effect — fires on every snapshot **data** transition only
  // (deps are ``[map, snapshot]``, NOT the colour/glyph fields). On the first
  // non-null snapshot :func:`attachSnapshotLayer` adds the source + layer; on
  // subsequent snapshots it calls :meth:`GeoJSONSource.setData` against the
  // already-registered source. No teardown between updates.
  //
  // Colour/glyph are deliberately NOT in this effect's deps: a colour-mode /
  // single-colour / glyph change must not re-run ``setData`` here, which would
  // overwrite the live SSE-merged marker positions (``featureCollectionRef``)
  // with the older REST-snapshot positions and snap every marker BACK along its
  // track tail (the reported flicker). The colour/glyph re-paint is owned by
  // the dedicated style effect below (paint/layout only). On a genuine snapshot
  // change the current colour/glyph are still applied — ``attachSnapshotLayer``
  // re-paints in place via :func:`reapplySnapshotPlatformStyle` — so the
  // first-attach paint is correct; we pass the live colour/glyph (read from a
  // ref so they aren't effect deps).
  const platformGlyphRef = useRef(platformGlyph);
  platformGlyphRef.current = platformGlyph;
  const platformColorModeRef = useRef(platformColorMode);
  platformColorModeRef.current = platformColorMode;
  const platformSingleColorRef = useRef(platformSingleColor);
  platformSingleColorRef.current = platformSingleColor;
  const platformStatusColorsRef = useRef(platformStatusColors);
  platformStatusColorsRef.current = platformStatusColors;
  useEffect(() => {
    if (snapshot === null) {
      // First-load / refetch-in-flight — keep the existing source
      // (if any) visible rather than clearing. The feature ref
      // also stays so SSE deltas continue to merge against the
      // last good snapshot.
      return;
    }
    attachSnapshotLayer(
      map,
      snapshot,
      {
        mode: platformColorModeRef.current,
        singleColor: platformSingleColorRef.current,
        statusColors: platformStatusColorsRef.current,
      },
      platformGlyphRef.current,
    );
    featureCollectionRef.current = narrowToMarkerCollection(snapshotToFeatureCollection(snapshot));
  }, [map, snapshot]);

  // Colour/glyph re-paint effect (REQ-UI-086) — fires when the Profile's
  // platform colour mode / single colour / glyph changes (or a theme flip
  // re-renders with the same values; the re-apply is idempotent). Updates the
  // marker layer's paint + (only-if-changed) ``icon-image`` IN PLACE via
  // :func:`reapplySnapshotPlatformStyle` — it never touches the source data, so
  // the live SSE-merged positions stay put and the markers do not flicker.
  // Keyed on ``snapshot`` too so the colour re-applies right after a first
  // attach / source rebuild (the layer exists only once the data effect ran).
  useEffect(() => {
    // Read the override map via the ref so the deps array stays honest: the
    // reactive trigger is ``platformStatusColorsKey`` (a pure function of the
    // map), and ``ref.current`` holds the same map this render computed the key
    // from — so the painted colours always match the dep that fired (mirrors the
    // ``anchorKey`` idiom in :func:`use-trail.useTrail`). A per-status override
    // change therefore repaints in place via ``setPaintProperty`` (no
    // ``setData``), so live SSE marker positions never flicker.
    reapplySnapshotPlatformStyle(
      map,
      {
        mode: platformColorMode,
        singleColor: platformSingleColor,
        statusColors: platformStatusColorsRef.current,
      },
      platformGlyph,
    );
  }, [
    map,
    snapshot,
    platformColorMode,
    platformSingleColor,
    platformStatusColorsKey,
    platformGlyph,
  ]);

  // Selection-highlight effect — point the ring at the selected platform
  // (REQ-UI-051; storyboard Flow Q §"Frame 2"). Keyed on ``snapshot`` too so
  // the filter is re-applied after a first attach / source rebuild (mirrors
  // the event layer's highlight effect). ``setSelectedPlatformHighlight``
  // no-ops until the highlight layer is registered.
  useEffect(() => {
    setSelectedPlatformHighlight(map, selectedPlatformId);
  }, [map, selectedPlatformId, snapshot]);

  // NOTE: the platform list "Select on Map" force-recenter is NOT here. The
  // snapshot's ``featureCollectionRef`` is viewport-scoped, so it could only
  // recenter to an already-on-screen platform — useless for jumping to an
  // off-screen one. That force-recenter now lives in
  // :func:`map/use-platform-focus-fly-to.usePlatformFocusFlyTo` (mounted in
  // :file:`Map.tsx`), which fetches the platform's position from the
  // producer-scoped ``/latest-status`` endpoint and eases the camera. After
  // the ease, the viewport change drives a fresh snapshot that renders the
  // platform, and the highlight effect above rings it. (REQ-UI-096; ADR-024 §5.)

  // SSE delta-merge — runs once per arriving SSE message, synchronously.
  // Only ``status``-kind messages affect the snapshot source; other kinds
  // are routed elsewhere.
  //
  // The handler reads the latest collection from the ref, merges, writes
  // back to the ref, and calls :func:`setData` on the already-registered
  // source. If the source isn't registered yet (the snapshot hasn't loaded)
  // the message is dropped — the next snapshot pulls the latest state from
  // the BFF.
  //
  // Merging through the ref (not React state) is what makes a burst safe:
  // each message in a flush merges on top of the previous one's result, so
  // several Producers' positions all land instead of only the last.
  useSseMessages(subscribe, (message) => {
    if (message.kind !== "status") {
      return;
    }
    const current = featureCollectionRef.current;
    if (current === null) {
      // Snapshot not yet loaded; the next snapshot fetch carries
      // this Producer's latest state — drop the message rather than
      // partially-populating the source.
      return;
    }
    // An SSE delta can land during the "Reset to profile" panel rebuild while
    // the map is mid-teardown; ``getSource`` would then throw on the removed
    // map's deleted style. Drop the delta safely (see :file:`map/map-ready.ts`).
    if (!isMapUsable(map)) {
      return;
    }
    const source = map.getSource(SNAPSHOT_SOURCE_ID) as GeoJSONSource | undefined;
    if (source === undefined) {
      // Layer was detached between the snapshot effect's cleanup
      // and this handler firing. Defensive: drop the message rather
      // than crash. The next snapshot will repopulate.
      return;
    }
    const merged = mergeStatusIntoFeatureCollection(current, message.payload);
    featureCollectionRef.current = merged;
    source.setData(merged);
  });

  return null;
}
